import { useState } from 'react'
import { getCategories, addCategory } from '../../data/categories'
import { BAKERY_CATEGORIES_SEED } from '../../data/bakeryData'
import { YAWEHS_CATEGORIES_SEED } from '../../data/yawehsData'
import ArithmeticInput from '../ui/ArithmeticInput'

function getCategoriesForDept(dept) {
  if (dept === 'yawehs') return YAWEHS_CATEGORIES_SEED
  if (dept === 'bakery') return BAKERY_CATEGORIES_SEED
  return getCategories()
}
import { addProduct, getProducts } from '../../data/products'
import { getDepartmentBranch } from '../../lib/productPricing'
import { useToast } from '../../context/ToastContext'
import { X, Plus } from 'lucide-react'

const NEW_CAT_VALUE = '__new__'

const SALE_TYPES = [
  { value: 'discrete', label: 'Discrete (units)' },
  { value: 'continuous_mirror', label: 'Continuous Mirror (R only)' },
  { value: 'continuous_fixed', label: 'Continuous Fixed (R + qty)' },
  { value: 'fixed_unit', label: 'Fixed Unit Price' },
]

const UNIT_OPTIONS = [
  { value: 'units', label: 'Units' },
  { value: 'rands', label: 'Rands (R)' },
  { value: 'kg', label: 'Kilograms (kg)' },
]

export default function AddProductModal({ onClose, onProductAdded, prefillName = '', department = 'kitchen' }) {
  const { addToast } = useToast()
  const primaryBranch = getDepartmentBranch(department) || 'limpopo'
  const secondaryBranch = primaryBranch === 'limpopo' ? 'pretoria' : 'limpopo'
  const [form, setForm] = useState({
    code: '',
    name: prefillName,
    category: getCategoriesForDept(department)[0]?.name || '',
    unitPrice: '',
    qps: '',
    saleType: 'discrete',
    unit: 'units',
    papCost: '',
    limpopoEnabled: primaryBranch === 'limpopo',
    pretoriaEnabled: primaryBranch === 'pretoria',
    limpopoPrice: '',
    pretoriaPrice: '',
  })
  const [addingNewCat, setAddingNewCat] = useState(false)
  const [newCatName, setNewCatName] = useState('')

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      addToast('Code and Name are required', 'error')
      return
    }

    if (addingNewCat && !newCatName.trim()) {
      addToast('Enter a category name or pick an existing one', 'error')
      return
    }

    // Create new category if user typed one
    let categoryName = form.category
    if (addingNewCat && newCatName.trim()) {
      const catKey = newCatName.trim().toLowerCase()
      const catLabel = newCatName.trim()
      addCategory({
        name: catKey,
        label: catLabel,
        openingUnit: 'units',
        surplusUnit: 'units',
        salesUnit: 'rands',
        projectable: true,
        type: 'discrete',
      })
      categoryName = catKey
    }

    const existing = getProducts(department)
    if (existing.find(p => p.code.toLowerCase() === form.code.trim().toLowerCase())) {
      addToast(`Product code "${form.code}" already exists`, 'error')
      return
    }

    if (!form.limpopoEnabled && !form.pretoriaEnabled) {
      addToast('Select at least one branch (Limpopo or Pretoria)', 'error')
      return
    }

    const bothEnabled = form.limpopoEnabled && form.pretoriaEnabled
    const limpopoNum = bothEnabled
      ? (form.limpopoPrice !== '' ? Number(form.limpopoPrice) : null)
      : (form.limpopoEnabled && form.unitPrice !== '' ? Number(form.unitPrice) : null)
    const pretoriaNum = bothEnabled
      ? (form.pretoriaPrice !== '' ? Number(form.pretoriaPrice) : null)
      : (form.pretoriaEnabled && form.unitPrice !== '' ? Number(form.unitPrice) : null)

    const prices = {
      limpopo: form.limpopoEnabled ? limpopoNum : null,
      pretoria: form.pretoriaEnabled ? pretoriaNum : null,
    }

    // Keep legacy unitPrice pointing at the primary branch for backward compat.
    const primaryPrice = prices[primaryBranch] ?? prices[secondaryBranch] ?? null

    try {
    const product = await addProduct({
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      category: categoryName,
      unitPrice: primaryPrice,
      prices,
      qps: form.qps ? Number(form.qps) : null,
      saleType: form.saleType,
      unit: form.unit,
      ...(department === 'kitchen' && form.papCost ? { papCost: Number(form.papCost) } : {}),
    }, department)

    addToast(`Added "${product.name}" (${product.code})`, 'success')
    onProductAdded?.(product)
    onClose()
    } catch (err) {
      addToast(`Failed to add product: ${err.message}`, 'error')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Add New Product</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Code *</label>
            <input
              value={form.code}
              onChange={e => set('code', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              placeholder="e.g. FMK"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Name *</label>
            <input
              value={form.name}
              onChange={e => set('name', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              placeholder="e.g. Full Mukokoroshi"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Category</label>
            {addingNewCat ? (
              <div className="flex gap-1">
                <input
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                  placeholder="e.g. Muroho"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => { setAddingNewCat(false); setNewCatName('') }}
                  className="px-2 text-gray-400 hover:text-white text-xs"
                >Cancel</button>
              </div>
            ) : (
              <select
                value={form.category}
                onChange={e => {
                  if (e.target.value === NEW_CAT_VALUE) {
                    setAddingNewCat(true)
                  } else {
                    set('category', e.target.value)
                  }
                }}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              >
                {getCategoriesForDept(department).map(c => (
                  <option key={c.name} value={c.name}>{c.label}</option>
                ))}
                <option value={NEW_CAT_VALUE}>+ New Category</option>
              </select>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Unit</label>
            <select
              value={form.unit}
              onChange={e => set('unit', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
            >
              {UNIT_OPTIONS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Sale Type</label>
          <select
            value={form.saleType}
            onChange={e => set('saleType', e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
          >
            {SALE_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Branch availability</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
              <input
                type="checkbox"
                checked={form.limpopoEnabled}
                onChange={e => set('limpopoEnabled', e.target.checked)}
                className="rounded bg-gray-800 border-gray-600"
              />
              🏔 Limpopo
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
              <input
                type="checkbox"
                checked={form.pretoriaEnabled}
                onChange={e => set('pretoriaEnabled', e.target.checked)}
                className="rounded bg-gray-800 border-gray-600"
              />
              🏙 Pretoria
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {form.limpopoEnabled && form.pretoriaEnabled ? (
            <>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Limpopo price (R)</label>
                <ArithmeticInput
                  min="0"
                  value={form.limpopoPrice}
                  onChange={(val) => set('limpopoPrice', val == null ? '' : val)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Pretoria price (R)</label>
                <ArithmeticInput
                  min="0"
                  value={form.pretoriaPrice}
                  onChange={(val) => set('pretoriaPrice', val == null ? '' : val)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                  placeholder="Optional"
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Unit Price (R)</label>
              <ArithmeticInput
                min="0"
                value={form.unitPrice}
                onChange={(val) => set('unitPrice', val == null ? '' : val)}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                placeholder="Optional"
              />
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-400 mb-1">QPS</label>
            <ArithmeticInput
              min="0"
              value={form.qps}
              onChange={(val) => set('qps', val == null ? '' : val)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              placeholder="Optional"
            />
          </div>
        </div>

        {department === 'kitchen' && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">Pap Cost (R)</label>
            <ArithmeticInput
              min="0"
              value={form.papCost}
              onChange={(val) => set('papCost', val == null ? '' : val)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              placeholder="0.00"
            />
            <p className="text-[10px] text-gray-500 mt-1">Optional — enter the rand value of pap served with this product</p>
          </div>
        )}

        <button
          onClick={handleSave}
          className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium"
        >
          <Plus size={16} /> Add Product
        </button>
      </div>
    </div>
  )
}
